import React, { useState, useEffect } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { AnalysisResult, AppState, User, HistoryItem, AdvertisingModalConfig } from './types';
import { analyzeText } from './services/geminiService';
import { subscribeToHistory, saveToHistory, clearHistory } from './services/historyService';
import { subscribeToAuthChanges, logout, handleRedirectResult } from './services/authService';
import { checkRateLimit, recordUsage } from './services/rateLimitService';
import { isAdmin, getModalConfig } from './services/adminService';
import { startVersionCheck, stopVersionCheck, getAppVersion } from './services/versionService';
import ScoreGauge from './components/ScoreGauge';
import TextHighlighter from './components/TextHighlighter';
import Checklist from './components/Checklist';
import Login from './components/Login';
import HistorySidebar from './components/HistorySidebar';
import AdminDashboard from './components/AdminDashboard';
import AdvertisingModal from './components/AdvertisingModal';
import TermsConditions from './components/TermsConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import { Clock, Plus, Shield, LogOut, Linkedin, Instagram, Eraser, Sparkles, Shuffle } from 'lucide-react';

const MEDICAL_EXAMPLES = [
  "Se comunica por medio de la presente que su solicitud de reintegro Nro 45992 ha sido rechazada por falta de documentación respaldatoria (Factura tipo C según RG AFIP). Deberá iniciar nuevo expediente por mesa de entradas en el plazo perentorio de 48hs.",
  "El paciente requiere realizar estudio de RMN de columna lumbosacra con contraste endovenoso previo a interconsulta con neurocirugía. Presentar orden médica sellada y firmada por traumatología en ventanilla 3 antes del 15/12. Sin excepción.",
  "Se informa que el tratamiento kinesiológico solicitado no se encuentra contemplado en el PMO vigente. Para evaluación de cobertura excepcional, el afiliado deberá presentar informe del médico tratante con justificación clínica fundamentada ante auditoría médica.",
  "La autorización de medicación de alto costo requiere presentación de protocolo completo incluyendo: HC, estudios complementarios, consentimiento informado y presupuesto farmacia habilitada. Plazo de respuesta: 72hs hábiles desde recepción completa de documentación.",
  "Se le recuerda que la consulta programada del 18/12 a las 14:30hs con cardiología clínica requiere presentación con ayuno de 12 horas, resultados de laboratorio (hemograma, perfil lipídico, glucemia) no mayor a 30 días y ECG previo. Concurrir con DNI y credencial."
];

const getRandomExample = () => MEDICAL_EXAMPLES[Math.floor(Math.random() * MEDICAL_EXAMPLES.length)];

const DEFAULT_TEXT = getRandomExample();

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [inputText, setInputText] = useState('');
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [appState, setAppState] = useState<AppState>(AppState.IDLE);
  const [errorMsg, setErrorMsg] = useState<string>("");

  // History State
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // Admin State
  const [isAdminDashboardOpen, setIsAdminDashboardOpen] = useState(false);

  // Advertising Modal State
  const [modalConfig, setModalConfig] = useState<AdvertisingModalConfig | null>(null);
  const [showAdvertisingModal, setShowAdvertisingModal] = useState(false);
  const [analysisCount, setAnalysisCount] = useState(0);

  // Set random placeholder on mount
  useEffect(() => {
    setInputText(getRandomExample());
  }, []);

  // Start version checking when user is authenticated
  useEffect(() => {
    if (user) {
      console.log(`🚀 Clara AI v${getAppVersion()} - Version checking enabled`);
      startVersionCheck();
      return () => stopVersionCheck();
    }
  }, [user]);

  // Handle redirect result from Google Sign-In
  useEffect(() => {
    handleRedirectResult().catch(error => {
      console.error('Error handling redirect:', error);
    });
  }, []);

  // Monitor Auth State
  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((currentUser) => {
      setUser(currentUser);
      setIsAuthChecking(false);
    });
    return () => unsubscribe();
  }, []);

  // Load history and modal config when user logs in
  useEffect(() => {
    if (user) {
      const unsubscribeHistory = subscribeToHistory(user.id, (items) => {
        setHistory(items);
      });

      // Load modal config
      getModalConfig().then(config => {
        setModalConfig(config);
      });

      // Load session analysis count
      const sessionCount = sessionStorage.getItem('clara_ai_analysis_count');
      if (sessionCount) {
        setAnalysisCount(parseInt(sessionCount, 10));
      }

      return () => unsubscribeHistory();
    } else {
      setHistory([]);
    }
  }, [user]);

  const handleLoginSuccess = (userProfile: User) => {
    setUser(userProfile);
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
    resetApp();
    setIsHistoryOpen(false);
  };

  const shouldShowModal = (): boolean => {
    if (!modalConfig?.enabled) return false;

    const lastShown = localStorage.getItem('clara_ai_modal_last_shown');
    const now = Date.now();

    switch (modalConfig.frequency) {
      case 'always':
        return true;
      case 'once_per_session':
        return !sessionStorage.getItem('clara_ai_modal_shown');
      case 'once_per_day':
        return !lastShown || now - parseInt(lastShown, 10) > 24 * 60 * 60 * 1000;
      case 'once_per_week':
        return !lastShown || now - parseInt(lastShown, 10) > 7 * 24 * 60 * 60 * 1000;
      case 'every_2_analyses':
        return (analysisCount + 1) % 2 === 0;
      case 'every_3_analyses':
        return (analysisCount + 1) % 3 === 0;
      case 'every_5_analyses':
        return (analysisCount + 1) % 5 === 0;
      default:
        return false;
    }
  };

  const markModalShown = () => {
    sessionStorage.setItem('clara_ai_modal_shown', 'true');
    localStorage.setItem('clara_ai_modal_last_shown', Date.now().toString());
  };

  const handleAnalyze = async () => {
    if (!inputText.trim()) return;

    // Rate Limit Check
    const limitStatus = checkRateLimit();
    if (!limitStatus.allowed) {
      setErrorMsg(`Has alcanzado el límite de uso. Por favor esperá ${limitStatus.waitTimeMinutes} minuto(s) para realizar un nuevo análisis.`);
      setAppState(AppState.ERROR);
      return;
    }

    setAppState(AppState.ANALYZING);
    setResult(null);
    setErrorMsg("");

    try {
      recordUsage();
      const data = await analyzeText(inputText);
      setResult(data);

      // Update analysis count
      const newCount = analysisCount + 1;
      setAnalysisCount(newCount);
      sessionStorage.setItem('clara_ai_analysis_count', newCount.toString());

      // Check if we should show the advertising modal
      if (shouldShowModal()) {
        markModalShown();
        setShowAdvertisingModal(true);
      } else {
        setAppState(AppState.RESULTS);
      }

      // Save to history
      if (user) {
        await saveToHistory(user.id, inputText, data);
      }
    } catch (error) {
      console.error(error);
      setAppState(AppState.ERROR);
      setErrorMsg("Ocurrió un error al conectar con la IA. Por favor intenta de nuevo.");
    }
  };

  const handleModalClose = () => {
    setShowAdvertisingModal(false);
    setAppState(AppState.RESULTS);
  };

  const resetApp = () => {
    setResult(null);
    setAppState(AppState.IDLE);
    setInputText(getRandomExample());
    setErrorMsg("");
  };

  const clearTextbox = () => {
    setInputText('');
  };

  const randomizeExample = () => {
    setInputText(getRandomExample());
  };

  const loadHistoryItem = (item: HistoryItem) => {
    setInputText(item.inputText);
    setResult(item.result);
    setAppState(AppState.RESULTS);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearHistory = async () => {
    if (confirm('¿Estás seguro de querer borrar todo el historial?')) {
      if (user) {
        await clearHistory(user.id);
      }
      setHistory([]);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Show loading screen while checking auth
  if (isAuthChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-sky-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-600 font-medium">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="*" element={<Login onLoginSuccess={handleLoginSuccess} />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/terms" element={<TermsConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="*" element={
        <div className="min-h-screen bg-slate-50 text-slate-800 font-sans pb-20 relative overflow-hidden">

          {/* Background Ambient Effects */}
          <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
            <div className="absolute -top-40 -left-40 w-96 h-96 bg-sky-200/30 rounded-full blur-3xl" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-100/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-teal-200/30 rounded-full blur-3xl" />
          </div>

          {/* Advertising Modal */}
          {showAdvertisingModal && modalConfig && (
            <AdvertisingModal
              isOpen={showAdvertisingModal}
              config={modalConfig}
              onClose={handleModalClose}
            />
          )}

          {/* Admin Dashboard */}
          {isAdminDashboardOpen && (
            <AdminDashboard
              isOpen={isAdminDashboardOpen}
              onClose={() => setIsAdminDashboardOpen(false)}
            />
          )}

          <HistorySidebar
            isOpen={isHistoryOpen}
            onClose={() => setIsHistoryOpen(false)}
            history={history}
            onSelect={loadHistoryItem}
            onClear={handleClearHistory}
          />

          {/* Header */}
          <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 via-sky-500 to-emerald-500 p-0.5 shadow-md shadow-sky-200/50">
                  <div className="w-full h-full bg-white rounded-[10px] flex items-center justify-center overflow-hidden">
                    <img
                      src="/logo-empatia.png"
                      alt="Clara AI"
                      className="w-6 h-6 object-contain"
                    />
                  </div>
                </div>
                <div className="flex flex-col">
                  <h1 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-600 to-emerald-500 leading-tight">
                    Clara AI
                  </h1>
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-medium">
                    <span>by</span>
                    <a
                      href="https://masempatia.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-600 hover:text-sky-700 font-semibold transition-colors"
                    >
                      Empatía
                    </a>
                    <div className="flex items-center gap-1 border-l border-slate-300 pl-1.5 ml-0.5">
                      <a
                        href="https://www.linkedin.com/in/marielasporn"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-sky-600 transition-colors"
                        title="LinkedIn"
                      >
                        <Linkedin className="w-3 h-3" />
                      </a>
                      <a
                        href="https://instagram.com/masempatia.ok"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-pink-500 transition-colors"
                        title="Instagram"
                      >
                        <Instagram className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Admin Dashboard Button (only for admins) */}
                {isAdmin(user) && (
                  <button
                    onClick={() => setIsAdminDashboardOpen(true)}
                    className="p-2 text-slate-500 hover:text-sky-600 hover:bg-slate-50 rounded-full transition-all"
                    title="Admin Dashboard"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                  </button>
                )}

                {/* History Toggle */}
                <button
                  onClick={() => setIsHistoryOpen(true)}
                  className="p-2 text-slate-500 hover:text-sky-600 hover:bg-slate-50 rounded-full transition-all relative group"
                  title="Ver Historial"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                </button>

                {/* User Profile */}
                <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                  <img src={user.photoURL || 'https://via.placeholder.com/32'} alt="User" className="w-8 h-8 rounded-full border border-slate-200 bg-slate-100" />
                  <div className="hidden md:block text-xs">
                    <p className="font-semibold text-slate-700">{user.name}</p>
                    <button onClick={handleLogout} className="text-slate-400 hover:text-red-500 transition-colors">Cerrar Sesión</button>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

            {/* Intro */}
            {appState === AppState.IDLE && (
              <div className="mb-8 text-center max-w-3xl mx-auto animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-50 border border-sky-200 text-sky-600 text-xs font-bold uppercase tracking-widest mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse"></span>
                  100% Gratuito
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-b from-slate-900 via-slate-700 to-slate-500 mb-3 tracking-tight leading-tight pb-2">
                  Comunicación clara, mensajes que conectan.
                </h2>
                <p className="text-slate-500 text-base leading-relaxed">
                  La IA gratuita que traduce la complejidad médica, administrativa y de gestión en un lenguaje <span className="text-sky-600 font-semibold">simple y cercano</span>. De lo técnico a lo humano, en segundos.
                </p>
                <p className="text-slate-600 text-base mt-2 font-semibold">
                  Acompañá a los pacientes con empatía.
                </p>
              </div>
            )}

            {/* Input Section */}
            <section className="bg-white rounded-3xl shadow-xl border border-slate-100 p-2 mb-8 transition-all focus-within:ring-4 focus-within:ring-sky-100/50 focus-within:shadow-2xl focus-within:shadow-sky-100/20 relative group">
              {/* Ambient glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-sky-400 via-emerald-400 to-teal-400 rounded-3xl opacity-0 group-focus-within:opacity-20 blur-xl transition-opacity duration-500"></div>

              <div className="relative">
                <textarea
                  className="w-full h-40 p-6 rounded-2xl resize-none outline-none text-slate-700 placeholder-slate-400 text-lg leading-relaxed focus:bg-slate-50/50 transition-colors"
                  placeholder="Pega aquí el texto: un email de facturación, un rechazo de obra social, una indicación de estudio, etc..."
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                />
                <div className="px-4 pb-4 flex justify-between items-center mt-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide px-2">
                      {inputText.length} caracteres
                    </span>
                    <button
                      onClick={clearTextbox}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      title="Borrar texto"
                    >
                      <Eraser className="w-4 h-4" />
                    </button>
                    <button
                      onClick={randomizeExample}
                      className="p-2 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-all"
                      title="Cargar ejemplo aleatorio"
                    >
                      <Shuffle className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    onClick={handleAnalyze}
                    disabled={appState === AppState.ANALYZING || !inputText.trim()}
                    className={`
                  px-8 py-3.5 rounded-xl font-bold text-white shadow-lg transition-all transform hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 relative overflow-hidden
                  ${appState === AppState.ANALYZING
                        ? 'bg-slate-300 cursor-not-allowed shadow-none'
                        : 'bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-400 hover:to-emerald-400 shadow-sky-200/50 hover:shadow-xl hover:shadow-sky-300/40'}
                `}
                  >
                    {appState === AppState.ANALYZING ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Analizando con IA...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 animate-pulse hidden sm:inline" />
                        Humanizar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </section>

            {/* Error Message */}
            {appState === AppState.ERROR && (
              <div className="mb-8 p-4 bg-red-50 border border-red-200 text-red-600 rounded-2xl flex items-center gap-3 shadow-lg shadow-red-100/50 animate-in slide-in-from-top-2">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Clock className="w-5 h-5 text-red-500" />
                </div>
                <p className="font-medium">{errorMsg}</p>
              </div>
            )}

            {/* Loading Skeletons */}
            {appState === AppState.ANALYZING && (
              <div className="animate-fade-in space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                  {/* Left Column: Metrics Skeleton */}
                  <div className="md:col-span-1 flex flex-col gap-6">

                    {/* Score Gauge Skeleton */}
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                      <div className="flex justify-center mb-4">
                        <div className="w-48 h-48 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 animate-pulse"></div>
                      </div>
                      <div className="h-4 bg-slate-200 rounded-full w-3/4 mx-auto animate-pulse"></div>
                    </div>

                    {/* Overall Feedback Skeleton */}
                    <div className="p-5 bg-white rounded-2xl border border-slate-100 shadow-sm">
                      <div className="h-3 bg-slate-200 rounded-full w-24 mb-3 animate-pulse"></div>
                      <div className="space-y-2">
                        <div className="h-3 bg-slate-100 rounded-full w-full animate-pulse"></div>
                        <div className="h-3 bg-slate-100 rounded-full w-5/6 animate-pulse"></div>
                        <div className="h-3 bg-slate-100 rounded-full w-4/6 animate-pulse"></div>
                      </div>
                    </div>

                    {/* Checklist Skeleton */}
                    <div>
                      <div className="h-3 bg-slate-200 rounded-full w-32 mb-3 px-1 animate-pulse"></div>
                      <div className="space-y-3">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div key={i} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3">
                            <div className="w-5 h-5 rounded-full bg-slate-200 animate-pulse flex-shrink-0"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-3 bg-slate-100 rounded-full w-3/4 animate-pulse"></div>
                              <div className="h-2 bg-slate-50 rounded-full w-full animate-pulse"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Text Analysis Skeleton */}
                  <div className="md:col-span-2 space-y-6">

                    {/* Visual Diagnosis Skeleton */}
                    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-5 h-5 bg-slate-200 rounded animate-pulse"></div>
                        <div className="h-4 bg-slate-200 rounded-full w-40 animate-pulse"></div>
                      </div>
                      <div className="space-y-2 p-4 bg-slate-50 rounded-xl">
                        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                          <div key={i} className="h-3 bg-slate-200 rounded-full animate-pulse" style={{ width: `${60 + Math.random() * 40}%` }}></div>
                        ))}
                      </div>
                    </div>

                    {/* Humanized Version Skeleton */}
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 border border-emerald-100 shadow-sm">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="h-5 bg-emerald-200 rounded-full w-48 mb-2 animate-pulse"></div>
                          <div className="h-3 bg-emerald-100 rounded-full w-56 animate-pulse"></div>
                        </div>
                        <div className="w-24 h-10 bg-white rounded-xl animate-pulse"></div>
                      </div>
                      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-emerald-100/50 shadow-sm">
                        <div className="space-y-3">
                          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                            <div key={i} className="h-4 bg-slate-100 rounded-full animate-pulse" style={{ width: `${50 + Math.random() * 50}%` }}></div>
                          ))}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            )}

            {/* Results Section */}
            {result && appState === AppState.RESULTS && (
              <div className="animate-fade-in space-y-8">

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                  {/* Left Column: Metrics */}
                  <div className="md:col-span-1 flex flex-col gap-6">

                    {/* Score */}
                    <ScoreGauge score={result.score} />

                    {/* Overall Feedback Card */}
                    <div className="p-5 bg-white rounded-2xl border border-slate-100 shadow-sm">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Conclusión</h4>
                      <p className="text-slate-600 text-sm italic">
                        "{result.overallFeedback}"
                      </p>
                    </div>

                    {/* Checklist Component */}
                    <div>
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 px-1">Análisis de Cumplimiento</h4>
                      <Checklist items={result.checklist} />
                    </div>
                  </div>

                  {/* Right Column: Text Analysis */}
                  <div className="md:col-span-2 space-y-6">

                    {/* Visual Diagnosis */}
                    <TextHighlighter text={inputText} highlights={result.highlights} />

                    {/* Humanized Version */}
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 border border-emerald-100 shadow-sm relative overflow-hidden">
                      <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
                        <svg width="120" height="120" viewBox="0 0 24 24" fill="currentColor" className="text-emerald-600">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                        </svg>
                      </div>

                      <div className="relative z-10">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-emerald-800 text-lg font-bold flex items-center gap-2">
                              <span className="bg-emerald-200 text-emerald-700 p-1 rounded-md">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                              </span>
                              Versión Humanizada
                            </h3>
                            <p className="text-emerald-600 text-sm mt-1">Propuesta optimizada para enviar</p>
                          </div>
                          <button
                            onClick={() => copyToClipboard(result.rewrittenText)}
                            className="flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 rounded-xl text-sm font-semibold hover:bg-emerald-100 transition-colors shadow-sm"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
                            Copiar
                          </button>
                        </div>

                        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-emerald-100/50 shadow-sm text-lg leading-relaxed text-slate-800 whitespace-pre-line">
                          {result.rewrittenText}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

              </div>
            )}

            {/* Footer */}
            <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 py-3 z-20">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between text-xs text-slate-400">
                <span>© 2024 Clara AI by Empatía</span>
                <div className="flex items-center gap-4">
                  <Link to="/terms" className="hover:text-sky-600 transition-colors">Términos y Condiciones</Link>
                  <Link to="/privacy" className="hover:text-sky-600 transition-colors">Política de Privacidad</Link>
                </div>
              </div>
            </footer>
          </main>
        </div>
      } />
    </Routes>
  );
};

export default App;